import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  Output,
} from '@angular/core'
import { MatDialog } from '@angular/material/dialog'
import {
  AppointmentModel,
  AUTHORIZATION_STATUS,
  COLOR, eligibilityStatusColor,
  getAuthorizationStatusColor,
  getAuthorizationStatusText,
  getWorstAuthorizationStatusText,
  Patient,
  PatientInsurance,
  PatientInsuranceType,
  Permission,
  PermissionsService,
  ProgressIndicatorService,
  sortPatientInsurance,
} from '@mmx/shared'
import { TranslateService } from '@ngx-translate/core'
import { BehaviorSubject, filter, Subject, takeUntil } from 'rxjs'

import { ConfirmNecessityDialogComponent } from '../../confirm-necessity-dialog'

interface StatusState {
  tooltipText: string
  insuranceItems: {
    insurance: PatientInsurance
    status?: AUTHORIZATION_STATUS
    statusColor?: COLOR
  }[]
  statuses: {
    primary?: COLOR
    secondary?: COLOR
  }
}

@Component({
  selector: 'mmx-authorization-status-icon',
  templateUrl: 'authorization-status-icon.component.html',
  standalone: false,
})
export class AuthorizationStatusIconComponent implements OnChanges, OnDestroy {
  private onDestroy$ = new Subject<void>()
  public statusChanged = new EventEmitter<void>()

  @Input() patient?: Patient
  @Input() appointment?: AppointmentModel
  @Input() statuses?: AUTHORIZATION_STATUS[]
  @Input() workflow = false
  @Output() authorizationSaved = new EventEmitter<void>()

  public inProgress = false
  public state$ = new BehaviorSubject<StatusState>(this.getUpdatedState())
  public isAuthorizationRuleRequired$ =
    this.permissionsService.hasPermission('auth.manager')
  public readonly Permission = Permission
  public readonly AUTHORIZATION_STATUS = AUTHORIZATION_STATUS
  public readonly getAuthorizationStatusColor = getAuthorizationStatusColor
  public readonly getAuthorizationStatusText = getAuthorizationStatusText

  constructor(
    private snackService: ProgressIndicatorService,
    private permissionsService: PermissionsService,
    private dialog: MatDialog,
    private translate: TranslateService,
  ) {}

  public ngOnChanges() {
    this.update()
  }

  public ngOnDestroy(): void {
    this.onDestroy$.next()
    this.onDestroy$.complete()
  }

  public openConfirmDialog(
    insurance: PatientInsurance,
    status: AUTHORIZATION_STATUS,
  ) {
    this.dialog
      .open(ConfirmNecessityDialogComponent, {
        panelClass: 'dialog-form',
        data: {
          type: 'authorization',
          appointment: this.appointment,
          insurance,
          status,
        },
      })
      .afterClosed()
      .pipe(takeUntil(this.onDestroy$), filter(Boolean))
      .subscribe({
        next: () => {
          this.authorizationSaved.emit()
          this.snackService.snack({
            message: this.translate.instant('icons.authorizations.updated'),
            duration: 5000,
          })
        },
        error: () => {
          this.snackService.snack({
            message: this.translate.instant('icons.authorizations.error'),
            duration: 5000,
          })
        },
      })
  }

  private update(): void {
    this.state$.next(this.getUpdatedState())
  }

  private getUpdatedState(): StatusState {
    const authorizationStatuses = (this.appointment?.authorizations || []).map(
      (r) => r.status,
    )
    const insuranceItems = this.getInsuranceItems()

    const state: StatusState = {
      insuranceItems,
      statuses: {
        primary: insuranceItems.find(
          (item) => item.insurance.type === PatientInsuranceType.PRIMARY,
        )?.statusColor,
        secondary: insuranceItems.find(
          (item) => item.insurance.type === PatientInsuranceType.SECONDARY,
        )?.statusColor,
      },
      tooltipText: getWorstAuthorizationStatusText(authorizationStatuses),
    }

    return state
  }

  private getInsuranceItems() {
    const insurances = sortPatientInsurance(this.appointment?.insurances || [])
    return insurances.map((insurance) => {
      const auth = (this.appointment.authorizations || []).find(
        ({ insuranceId }) => insuranceId === insurance.id,
      )
      if (!auth) {
        return { insurance }
      }

      return {
        insurance,
        status: auth.status,
        statusColor: getAuthorizationStatusColor(auth.status),
      }
    })
  }

  protected readonly eligibilityStatusColor = eligibilityStatusColor
}
